if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
        this.measurementsDescription = this.querySelector('div.product-measurements');
        this.measurementsReadMore = this.querySelector('.product-measurements__read-more');
        this.accordianSizeGuideLink = this.querySelector?.('.size-guide-opener-link');
        this.stickyAddToCartContainer = document.querySelector('sticky-add-to-cart');
      }

      initElementReferences() {
        this.firstAccordionDetails = this.querySelectorAll('details')[0];
        this.firstDetailsSummary = this.querySelectorAll('summary')[0];
      }

      connectedCallback() {
        this.initElementReferences();
        this.initializeProductSwapUtility();
        this.initOpenFirstAccordionDetails();
        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
        this.attachMeasurementsReadMoreHandler();
      }

      /**
       * Initializes the first accordion details element by setting it to open state
       * and handles related elements like measurements description and size guide links.
       * @returns {void}
       */
      initOpenFirstAccordionDetails() {
        this.firstAccordionDetails?.setAttribute('open', 'true');
        this.firstDetailsSummary?.setAttribute('aria-expanded', 'true');

        if (this.measurementsDescription) {
          this.measurementsDescription.classList.add('display-none');
        }
        if (this.accordianSizeGuideLink && this.measurementsDescription) {
          this.accordianSizeGuideLink.classList.add('display-none');
        }
      }

      /**
       * Toggles the visibility of measurement descriptions and size guide links
       * when the read more/less button is clicked.
       * @returns {void}
       */
      toggleReadmoreContent() {
        if (this.measurementsDescription) {
          const isHidden = this.measurementsDescription.classList.toggle('display-none');
          this.accordianSizeGuideLink?.classList.toggle('display-none');

          // Update ARIA attributes for accessibility
          this.measurementsDescription.setAttribute('aria-hidden', isHidden ? 'true' : 'false');
          if (this.measurementsReadMore) {
            this.measurementsReadMore.setAttribute('aria-expanded', (!isHidden).toString());
          }

          if (isHidden) {
            this.measurementsReadMore.textContent = `${window.additionalProductInfoStrings.readMore}`;
          } else {
            this.measurementsReadMore.textContent = '';
          }
        }
      }

      /**
       * Attaches event handler to the measurements read more button.
       * Sets up click event handling for toggling measurements visibility.
       * @returns {void}
       */
      attachMeasurementsReadMoreHandler() {
        this.measurementsReadMore = this.querySelector('.product-measurements__read-more');

        if (this.measurementsReadMore) {
          this.measurementsReadMore.removeEventListener('click', this.toggleReadmoreContent.bind(this));
          this.measurementsReadMore.addEventListener('click', this.toggleReadmoreContent.bind(this));
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.mainCTAObserver?.disconnect();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );

        this.postProcessHtmlCallbacks.push((newNode) => {
          // Initialize core functionality
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();

          // Update product additional information
          this.updateProductAdditionalInfo(newNode);
        });
      }

      /**
       * Updates the product additional information content for both desktop and mobile views.
       * This method is called after a product swap or content update.
       * @param {Element} newNode - The new DOM node containing updated content
       * @returns {void}
       */
      updateProductAdditionalInfo(newNode) {
        if (!newNode) return;

        // Update view
        this.updateViewContent(newNode, 'js-product-additional-information');
      }

      /**
       * Updates the content for a specific view (desktop/mobile) if it has changed.
       * Handles content updates and reinitializes interactive elements.
       * @param {Element} newNode - The new DOM node containing updated content
       * @param {string} targetId - The js-target attribute value to identify the container
       * @param {string} viewType - The view type ('desktop' or 'mobile')
       * @returns {void}
       */
      updateViewContent(newNode, targetId) {
        // Find source and target containers
        const sourceContainer = newNode.querySelector(`[js-target="${targetId}"]`);
        const targetContainer = document.querySelector(`[js-target="${targetId}"]`);

        if (!sourceContainer || !targetContainer) return;

        // Update content if different
        if (sourceContainer.innerHTML !== targetContainer.innerHTML) {
          targetContainer.innerHTML = sourceContainer.innerHTML;

          // Re-initialize size guide links
          this.initializeSizeGuideLinks(targetContainer);
        }
      }

      /**
       * Updates the additional information and size guide content for both desktop and mobile views
       * when a variant changes.
       * @param {Document} html - The parsed HTML document containing the new content
       * @param {Object} variant - The currently selected product variant
       * @returns {void}
       */
      updateAdditionalInfoAndSizeGuide(html, variant) {
        // Find containers using js- attribute selectors that match the liquid template
        const container = document.querySelector('[js-target="js-product-additional-information"]');

        if (container) {
          // Get the new content using the same selectors
          const newInfo = html.querySelector('[js-target="js-product-additional-information"]');

          if (container && newInfo) {
            container.innerHTML = newInfo.innerHTML;

            // Re-initialize elements after content update
            this.measurementsDescription = this.querySelector('div.product-measurements');
            this.accordianSizeGuideLink = this.querySelector('.size-guide-opener-link');

            this.initializeSizeGuideLinks(document);
            this.initOpenFirstAccordionDetails();
            this.attachMeasurementsReadMoreHandler();
          }
        }
      }

      /**
       * Initializes or reinitializes click handlers for size guide links within a container
       * @param {Element} container - The container element to search for size guide links
       * @returns {void}
       */
      initializeSizeGuideLinks(container) {
        // Create the event handler that uses event delegation
        const clickHandler = (event) => {
          const link = event.target.closest('a[data-size-guide]');
          if (!link) return; // Not clicking on a size guide link

          event.preventDefault();

          // Try to find the size-guide custom element
          const sizeGuide = document.querySelector('size-guide');

          if (sizeGuide?.handleLinkClick) {
            sizeGuide.lastTriggerElement = link;
            sizeGuide.handleLinkClick();
          } else {
            console.log('Size guide component not found or handleLinkClick not available');
          }
        };

        // Store handler reference and add single listener at container level
        container._sizeGuideHandler = clickHandler;
        container.addEventListener('click', clickHandler);
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
            
            // Special handling for Doogma engraving container to enable/disable trigger button based on variant availability
            if (source?.id?.startsWith("doogma-engraving-container")) {
              if (window.productVariants) {
                const selectedVariant = window.productVariants?.find((variant) => variant.selected)
                const PDPTriggerBtn = document.querySelector("product-info .doogma__trigger-button")
                if(PDPTriggerBtn){
                  PDPTriggerBtn.disabled = !selectedVariant?.available
                }
              }
            }
          };

          // Update sticky bar elements
          updateSourceFromDestination('sticky-price');
          updateSourceFromDestination('sticky-buy-buttons');

          //update doogma data
          updateSourceFromDestination('doogma-engraving-container');
          updateSourceFromDestination('price');
          updateSourceFromDestination('price-button');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          // Update additional info and size guide
          this.updateAdditionalInfoAndSizeGuide(html, variant);
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        // Also update the sticky add-to-cart form variant input
        const stickyForm = document.querySelector(`#sticky-form-${this.dataset.section}`);
        if (stickyForm) {
          const stickyInput = stickyForm.querySelector('input[name="id"]');
          if (stickyInput) {
            stickyInput.value = variantId ?? '';
            stickyInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        if (this.stickyAddToCartContainer) {
          this.stickyAddToCartContainer.syncStickySubmitButton(true, window.variantStrings.unavailable);
        }

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}